import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BellIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { notificationAPI } from '../services/notificationAPI';
import type { NotificationPreferences } from '../services/notificationAPI';
import { useAuth } from '../contexts/AuthContext';

type Channel = keyof NotificationPreferences;
type Feedback = { channel: Channel; kind: 'saving' | 'success' | 'error'; message: string };

const NotificationSettings = () => {
  const { user } = useAuth();
  const location = useLocation();
  const section = useRef<HTMLElement>(null);
  const generation = useRef(0);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (location.hash === '#notifications') section.current?.scrollIntoView({ block: 'start' });
  }, [location.hash]);
  useEffect(() => {
    const version = ++generation.current;
    setPreferences(null); setLoadError(false); setFeedback(null);
    notificationAPI.preferences().then(({ data }) => {
      if (version === generation.current) setPreferences(data.preferences);
    }).catch(() => { if (version === generation.current) setLoadError(true); });
    return () => { generation.current += 1; };
  }, [reload, user?.id, user?.is_premium]);

  const saving = feedback?.kind === 'saving';
  const toggle = async (channel: Channel) => {
    if (!preferences || saving) return;
    const next = !preferences[channel];
    const version = generation.current;
    setFeedback({ channel, kind: 'saving', message: 'Saving…' });
    try {
      const { data } = await notificationAPI.setPreference(channel, next);
      if (version !== generation.current) return;
      setPreferences(data.preferences);
      const label = channel === 'daily_digest_email' ? 'Daily match emails' : channel === 'new_match_in_app' ? 'New match notifications' : 'Deadline reminders';
      setFeedback({ channel, kind: 'success', message: `Saved. ${label} are ${next ? 'on' : 'off'}.` });
    } catch {
      if (version === generation.current) setFeedback({ channel, kind: 'error', message: `Couldn’t save. Your setting is still ${preferences[channel] ? 'on' : 'off'}. Please try again.` });
    }
  };

  const control = (channel: Channel, title: string, label: string) => (
    <>
      <div className="mt-5 flex items-center justify-between gap-6 border-t border-slate-200 pt-4">
        <span className="flex items-center gap-2 font-medium text-slate-900">
          {channel === 'daily_digest_email' ? <EnvelopeIcon className="h-5 w-5" /> : <BellIcon className="h-5 w-5" />}{label}
        </span>
        <button type="button" role="switch" aria-checked={preferences?.[channel] ?? false} aria-label={title}
          disabled={!preferences || saving} onClick={() => void toggle(channel)}
          className={`relative h-6 w-11 shrink-0 rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50 ${preferences?.[channel] ? 'bg-blue-600' : 'bg-slate-300'}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${preferences?.[channel] ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>
      <div className="mt-3 min-h-5" aria-live="polite">
        {feedback?.channel === channel && <p role={feedback.kind === 'error' ? 'alert' : 'status'} className={`text-sm ${feedback.kind === 'error' ? 'text-red-700' : feedback.kind === 'success' ? 'text-green-700' : 'text-slate-500'}`}>{feedback.message}</p>}
      </div>
    </>
  );

  return (
    <section ref={section} id="notifications" className="lg:col-span-2">
      <h2 className="text-2xl font-semibold text-slate-900">Notifications</h2>
      <p className="mt-2 text-sm text-slate-600">Choose how you hear about opportunities.</p>
      {new URLSearchParams(location.search).get('unsubscribed') === 'daily' && <p role="status" className="mt-4 text-sm text-green-700">You’ve unsubscribed from daily matching emails.</p>}
      {loadError ? <p role="alert" className="mt-4 text-sm text-red-700">Unable to load notification settings. <button onClick={() => setReload(value => value + 1)} className="font-semibold underline">Try again</button></p>
        : !preferences ? <p role="status" className="mt-4 text-sm text-slate-500">Loading settings…</p>
        : <>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="font-semibold text-slate-900">Saved opportunity deadline reminders</h3>
            <p className="mt-2 text-sm text-slate-600">A reminder at 7 a.m. Eastern the day before a saved opportunity is due.</p>
            {control('saved_deadline_reminders', 'Saved opportunity deadline reminders in-app', 'In-app')}
          </div>
          {user?.is_premium && <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-slate-900">New opportunities matching your preferences</h3><span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">PRO</span></div>
            <p className="mt-2 text-sm text-slate-600">In-app alerts after each opportunity update, plus an optional daily email.</p>
            <Link to="/preferences" className="mt-3 inline-flex text-sm font-semibold text-blue-600 hover:underline">Edit Match Preferences →</Link>
            {control('new_match_in_app', 'New matching opportunities in-app', 'In-app')}
            {control('daily_digest_email', 'Daily digest email for new matching opportunities', 'Email · Daily digest')}
          </div>}
          <p className="mt-5 text-sm text-slate-500">Turning notifications off stops future delivery. Your notification history is kept.</p>
          <p className="mt-5 border-t border-slate-200 pt-4 text-sm text-slate-500">You’ll still receive essential account, security, and billing messages.</p>
        </>}
    </section>
  );
};

export default NotificationSettings;
